import { useNavigate } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import DroneIcon from '../../../components/icons/DroneIcon';
import Button from '../../../components/ui/Button';
import { formatNumber } from '../../../utils/formatNumber';
import MashhadLogo from '../../../components/MashhadLogo';
import {
  HIDDEN_BOOKING_PHOTOGRAPHY_TYPES,
  HIDDEN_PROFILE_SPECIALIZATION_VALUES,
  HIDDEN_SPECIALIZATION_LABELS_AR,
} from '../../../utils/photographyTypesVisibility';

const SHOOTING_METHOD_ICONS = {
  professional_camera: { Icon, name: 'Camera' },
  mobile_camera: { Icon, name: 'Smartphone' },
  drone: { Icon: DroneIcon, name: null },
};

// عرض أنواع التصوير بالعربي في بطاقة المصور فقط (لا يؤثر على القيم المخزنة)
const SPECIALIZATION_LABELS_AR = {
  events: 'تصوير المناسبات',
  portrait: 'التصوير الشخصي',
  food: 'تصوير الطعام',
  product: 'تصوير المنتجات',
};

const getExperienceLabel = (level) => {
  if (!level) return 'معتمد';
  if (typeof level === 'string' && (level.includes('معتمد') || level.includes('مميز') || level.includes('نخبة'))) return level;
  const levelMap = {
    certified: 'معتمد',
    distinguished: 'مميز',
    elite: 'نخبة',
    beginner: 'معتمد',
    intermediate: 'مميز',
    advanced: 'مميز',
    expert: 'نخبة'
  };
  return levelMap[level] || level;
};

const PhotographerCard = ({ photographer, priority = false }) => {
  const navigate = useNavigate();
  const photographerId = photographer?.id ?? photographer?.userId ?? photographer?.user_id;

  const handleCardClick = (e) => {
    if (e.target.closest('button')) return;
    if (photographerId) navigate(`/photographers/${photographerId}`);
  };

  const rawExperience = typeof photographer?.experienceLevel === 'string'
    ? photographer.experienceLevel
    : getExperienceLabel(photographer?.experienceLevel ?? photographer?.experience_level);
  // عرض مستوى الخبرة فقط دون عدد السنوات (إخفاء أي نص مثل "5 سنوات" أو "8+ سنوات")
  const experienceDisplay = String(rawExperience ?? '').replace(/\s*\([^)]*\)\s*$/, '').trim() || 'معتمد';
  const distanceDisplay = photographer?.distanceKm != null
    ? (photographer?.distanceLabel ?? `${Number(photographer.distanceKm).toFixed(1)} كم`)
    : '-- كم';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick(e); } }}
      className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 border border-slate-100 hover:border-primary/20 cursor-pointer"
    >
      {/* Portfolio Image Cover - نفس الشكل في الرئيسية */}
      <div className="relative h-64 md:h-72 overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200">
        {photographer?.portfolioImage ? (
          <Image
            src={photographer.portfolioImage}
            fallbackSrc={photographer.portfolioImageOriginal}
            priority={priority}
            alt={photographer?.portfolioImageAlt || 'صورة من معرض أعمال المصور'}
            className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:brightness-110"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
            <MashhadLogo size="xl" variant="dark" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black/60 via-black/30 to-transparent" />

        {/* Distance Badge */}
        <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-md text-slate-900 px-3 py-1.5 rounded-full flex items-center gap-1.5 text-xs font-semibold shadow-lg border border-white/50 z-10">
          <Icon name="Navigation" size={12} className="text-primary" />
          <span className="text-primary font-bold">{distanceDisplay}</span>
        </div>

        {/* Photographer Info Overlay */}
        <div className="absolute bottom-4 right-4 left-4 z-10 flex items-center gap-3">
          <div className="relative flex-shrink-0">
            <div className="absolute inset-0 bg-gradient-to-br from-[#1e40af]/30 via-[#0891b2]/30 to-[#475569]/30 backdrop-blur-sm rounded-full blur-md" />
            <Image
              src={photographer?.avatar || photographer?.image || photographer?.user?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(photographer?.name || 'مصور')}&background=1e40af&color=fff&size=128`}
              fallbackSrc={photographer?.avatarOriginal}
              priority={priority}
              alt={photographer?.avatarAlt || `صورة ${photographer?.name}`}
              className="relative w-16 h-16 md:w-20 md:h-20 rounded-full object-cover border-4 border-white shadow-2xl ring-2 ring-white/50"
            />
          </div>
          <div className="text-white flex-1 min-w-0" dir="rtl">
            <h3 className="text-base md:text-lg font-bold drop-shadow-lg line-clamp-1 mb-1 text-right">
              {photographer?.name}
            </h3>
            <div className="flex items-center gap-1.5 text-xs text-white/90 drop-shadow-md text-right">
              <Icon name="MapPin" size={12} className="flex-shrink-0" />
              <span className="line-clamp-1">{photographer?.city || 'الرياض'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Card Content */}
      <div className="p-4 md:p-5 bg-white">
        <div className="flex items-center justify-between gap-4 mb-3 pb-3 border-b border-slate-100">
          <div className="flex items-center gap-1.5">
            <Icon name="Star" size={16} className="text-amber-400 fill-amber-400" />
            <span className="font-bold text-slate-900 text-sm">{formatNumber(photographer?.rating ?? 0)}</span>
            <span className="text-slate-500 text-xs">({formatNumber(photographer?.reviewCount ?? photographer?.totalReviews ?? 0)})</span>
          </div>
          {photographer?.isVerified && (
            <div className="flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-700 rounded-full text-xs font-medium">
              <Icon name="BadgeCheck" size={12} />
              <span>موثق</span>
            </div>
          )}
        </div>

        {/* مستوى الخبرة + وسائل التصوير في صف واحد (بدون عرض عدد السنوات) */}
        <div className="mb-3 pb-3 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-slate-500">مستوى الخبرة:</span>
            {(() => {
              const level = experienceDisplay;
              const isElite = level.includes('نخبة');
              const isDist  = level.includes('مميز');
              const iconName = isElite ? 'Crown' : isDist ? 'Star' : 'BadgeCheck';
              const badgeCls = isElite
                ? 'bg-violet-100 text-violet-700 border-violet-300'
                : isDist
                ? 'bg-amber-100 text-amber-700 border-amber-300'
                : 'bg-emerald-100 text-emerald-700 border-emerald-300';
              return (
                <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-semibold ${badgeCls}`}>
                  <Icon name={iconName} size={12} />
                  <span>{level}</span>
                </div>
              );
            })()}
          </div>
          {(photographer?.shootingMethods || photographer?.shooting_methods)?.length > 0 && (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10 shadow-sm">
              {(Array.isArray(photographer.shootingMethods) ? photographer.shootingMethods : photographer.shooting_methods || []).map((method) => {
                const config = SHOOTING_METHOD_ICONS[method];
                if (!config) return null;
                const IconComponent = config.Icon;
                return (
                  <span key={method} className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/80 text-primary border border-primary/10">
                    {config.name ? (
                      <IconComponent name={config.name} size={16} />
                    ) : (
                      <IconComponent size={16} className="text-primary" />
                    )}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {/* تخصصات + عداد الباقي — نفس منطق مصورين بالقرب منك */}
        {(() => {
          const rawSpec = photographer?.specialties ?? photographer?.specializations ?? [];
          const specialtiesList = Array.isArray(rawSpec) ? rawSpec : (typeof rawSpec === 'string' ? rawSpec.split(',').map((s) => s.trim()).filter(Boolean) : []);
          const visibleSpecs = specialtiesList.filter(
            (s) =>
              !HIDDEN_BOOKING_PHOTOGRAPHY_TYPES.has(s) &&
              !HIDDEN_PROFILE_SPECIALIZATION_VALUES.has(s) &&
              !HIDDEN_SPECIALIZATION_LABELS_AR.has(s)
          );
          return (
            <div className="flex flex-wrap gap-1.5 mb-3 items-center">
              {visibleSpecs.slice(0, 3).map((specialty, index) => {
                const labelAr = SPECIALIZATION_LABELS_AR[specialty] || specialty;
                return (
                  <span
                    key={index}
                    className="px-2 py-1 bg-gradient-to-r from-primary/10 to-primary/5 text-primary text-xs font-semibold rounded-lg border border-primary/20"
                  >
                    {labelAr}
                  </span>
                );
              })}
              {visibleSpecs.length > 3 && (
                <span className="text-xs font-semibold text-slate-500">(+{visibleSpecs.length - 3})</span>
              )}
            </div>
          );
        })()}

        <Button
          variant="outline"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (photographerId) navigate(`/photographers/${photographerId}`);
          }}
          className="w-full"
          iconName="User"
          iconPosition="right"
        >
          عرض الملف
        </Button>
      </div>
    </div>
  );
};

export default PhotographerCard;
